import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Shield, Building2, Users, Trash2, Move, Plus, LogOut, X, Loader2,
  Search, Check, ClipboardCopy, ScrollText, FileBarChart2, UserCheck, Inbox, Sparkles,
  TrendingUp, Activity, Clock, AlertCircle, Pencil, Settings as SettingsIcon,
} from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip,
  PieChart, Pie, Cell, Legend,
} from "recharts";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * Super Admin console — role="admin" only. Standalone screen, no accountant
 * sidebar. Manages every client + company on the platform.
 */
export default function SuperAdmin() {
  const [overview, setOverview] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [clients, setClients] = useState([]);
  const [unassigned, setUnassigned] = useState([]);
  const [companyFilter, setCompanyFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [moving, setMoving] = useState(null); // client being moved
  const [editing, setEditing] = useState(null); // client being edited
  const [editingCompany, setEditingCompany] = useState(null); // firm being edited
  const [hideEmpty, setHideEmpty] = useState(false);
  const [showLog, setShowLog] = useState(false);
  const { logout, user } = useAuth();

  const load = async () => {
    setLoading(true);
    try {
      const [o, c, cl, un] = await Promise.all([
        api.get("/super-admin/overview"),
        api.get("/super-admin/companies"),
        api.get("/super-admin/clients", {
          params: {
            company_id: companyFilter === "all" ? undefined : companyFilter,
            q: query || undefined,
          },
        }),
        api.get("/super-admin/unassigned"),
      ]);
      setOverview(o.data);
      setCompanies(c.data.companies || []);
      setClients(cl.data.clients || []);
      setUnassigned(un.data.clients || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Load failed");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [companyFilter]);

  // Debounced search
  useEffect(() => {
    const t = setTimeout(() => { load(); }, 300);
    return () => clearTimeout(t);
  }, [query]);

  const remove = async (c) => {
    if (!window.confirm(`Delete client "${c.business_name}" and all their data? This cannot be undone.`)) return;
    try {
      const { data } = await api.delete(`/super-admin/clients/${c.id}`);
      toast.success(`Deleted. Removed ${data.cascade_counts.statements} statements, ${data.cascade_counts.tasks} tasks, ${data.cascade_counts.notes} notes.`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed");
    }
  };

  const impersonateCompany = async (c) => {
    if (!window.confirm(`Sign in as ${c.business_name || c.email}? You'll see their app exactly as they do. Return via the "Exit" banner at the top.`)) return;
    try {
      await api.post(`/super-admin/companies/${c.id}/impersonate`);
      window.location.href = "/app";
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not impersonate");
    }
  };

  const impersonateClient = async (c) => {
    if (!window.confirm(`Sign in as client "${c.business_name || c.email}"? You'll see the client portal exactly as they do. Return via the "Exit" banner at the top.`)) return;
    try {
      await api.post(`/super-admin/clients/${c.id}/impersonate`);
      window.location.href = "/client";
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not impersonate");
    }
  };

  const removeCompany = async (c) => {
    if (!window.confirm(`Delete firm "${c.business_name || c.email}"? This cannot be undone.\n\nIf they still have clients, you'll need to move or delete those first.`)) return;
    try {
      const { data } = await api.delete(`/super-admin/companies/${c.id}`);
      toast.success(`Firm deleted. Removed ${data.cascade_counts.statements} statements, ${data.cascade_counts.tasks} tasks, ${data.cascade_counts.notes} notes.`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed");
    }
  };

  const bulkCleanupEmpty = async () => {
    // Client-side preview count (matches server logic: exclude super_admin + firms with 0 clients)
    const candidates = companies.filter((c) => c.kind !== "super_admin" && (c.client_count || 0) === 0);
    if (candidates.length === 0) {
      toast.message("No empty firms to clean up.");
      return;
    }
    if (!window.confirm(`Delete ${candidates.length} firm(s) with 0 clients?\n\nOnly firms that also have 0 uploaded statements will actually be removed. This cannot be undone.`)) return;
    try {
      const { data } = await api.post("/super-admin/companies/bulk-cleanup-empty");
      toast.success(`Cleaned up ${data.deleted_count} empty firm(s).`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Bulk clean-up failed");
    }
  };

  return (
    <div className="min-h-screen bg-paper">
      {/* Top bar */}
      <div className="bg-white border-b border-border sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center gap-4">
          <div className="flex items-center gap-2 shrink-0">
            <div className="w-8 h-8 rounded-lg bg-ochre/10 text-ochre flex items-center justify-center">
              <Shield className="w-4 h-4" strokeWidth={2} />
            </div>
            <div>
              <div className="font-heading text-lg font-medium leading-none">Super Admin</div>
              <div className="text-[10px] text-ink-lighter mt-0.5">Platform console · {user?.email}</div>
            </div>
          </div>
          <div className="flex-1" />
          <button
            onClick={() => setShowLog(true)}
            className="btn-secondary text-sm inline-flex items-center gap-1.5"
            data-testid="sa-audit-log-btn"
          >
            <ScrollText className="w-3.5 h-3.5" strokeWidth={1.75} />
            Audit log
          </button>
          <Link
            to="/super-admin/settings"
            className="btn-secondary text-sm inline-flex items-center gap-1.5"
            data-testid="sa-settings-link"
          >
            <SettingsIcon className="w-3.5 h-3.5" strokeWidth={1.75} />
            Settings
          </Link>
          <button
            onClick={logout}
            className="btn-secondary text-sm inline-flex items-center gap-1.5"
            data-testid="sa-logout-btn"
          >
            <LogOut className="w-3.5 h-3.5" strokeWidth={1.75} />
            Sign out
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8 space-y-6" data-testid="super-admin-page">
        {/* Overview */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <OverviewTile label="Accountants" value={overview?.accountants} icon={Building2} testid="ov-accountants" tone="forest" />
          <OverviewTile label="Clients" value={overview?.clients} icon={Users} testid="ov-clients" tone="sky" />
          <OverviewTile label="Statements" value={overview?.statements} icon={FileBarChart2} testid="ov-statements" tone="ochre" />
          <OverviewTile label="Tasks" value={overview?.tasks} icon={FileBarChart2} testid="ov-tasks" tone="violet" />
          <OverviewTile
            label="Orphaned"
            value={overview?.orphaned_clients}
            icon={Users}
            testid="ov-orphaned"
            tone="rose"
            accent={overview?.orphaned_clients > 0 ? "text-ochre" : ""}
          />
        </div>

        <AnalyticsSection />

        {unassigned.length > 0 && (
          <UnassignedQueue
            clients={unassigned}
            accountants={companies.filter(c => c.kind !== "super_admin")}
            onAssigned={load}
          />
        )}

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Companies sidebar */}
          <div className="lg:col-span-1 card-surface overflow-hidden border border-black">
            <div className="px-4 py-3 border-b border-border">
              <div className="overline">Filter</div>
              <div className="font-heading text-lg font-medium">Companies</div>
              <div className="mt-2 flex items-center justify-between gap-2">
                <label className="inline-flex items-center gap-1.5 text-xs text-ink-light cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={hideEmpty}
                    onChange={(e) => setHideEmpty(e.target.checked)}
                    className="accent-forest"
                    data-testid="sa-hide-empty-toggle"
                  />
                  Hide empty firms
                </label>
                <button
                  type="button"
                  onClick={bulkCleanupEmpty}
                  className="text-[11px] text-ink-lighter hover:text-destructive underline decoration-dotted"
                  data-testid="sa-bulk-cleanup"
                  title="Delete every firm with 0 clients and 0 statements"
                >
                  Bulk clean-up
                </button>
              </div>
            </div>
            <div className="max-h-[70vh] overflow-y-auto">
              <button
                onClick={() => setCompanyFilter("all")}
                className={`w-full text-left px-4 py-3 border-b border-border text-sm ${
                  companyFilter === "all" ? "bg-forest/10 text-forest font-medium" : "hover:bg-paper/60"
                }`}
                data-testid="sa-company-all"
              >
                All clients <span className="text-xs text-ink-lighter">({overview?.clients || 0})</span>
              </button>
              {companies
                .filter((c) => !hideEmpty || c.kind === "super_admin" || (c.client_count || 0) > 0)
                .map((c) => {
                const isPlatform = c.kind === "super_admin";
                return (
                  <div
                    key={c.id}
                    className={`w-full border-b border-border ${
                      companyFilter === c.id ? "bg-forest/10" : ""
                    }`}
                  >
                    <button
                      onClick={() => setCompanyFilter(c.id)}
                      className={`w-full text-left px-4 py-3 hover:bg-paper/60 ${
                        companyFilter === c.id ? "text-forest" : ""
                      }`}
                      data-testid={`sa-company-${c.id}`}
                    >
                      <div className="text-sm font-medium truncate flex items-center gap-1.5">
                        {isPlatform && <Shield className="w-3.5 h-3.5 text-ochre shrink-0" strokeWidth={2} />}
                        {c.business_name || "(no name)"}
                      </div>
                      <div className="text-[11px] text-ink-lighter truncate">{c.email}</div>
                      <div className="text-[10px] text-ink-lighter mt-1 num">{c.client_count} clients · {c.statement_count} statements</div>
                    </button>
                    {!isPlatform && (
                      <div className="flex items-stretch border-t border-border/40 divide-x divide-border/40">
                        <button
                          onClick={() => setEditingCompany(c)}
                          className="flex-1 px-2 py-1.5 text-[11px] text-ink-lighter hover:text-forest hover:bg-forest/5 inline-flex items-center justify-center gap-1"
                          data-testid={`sa-company-edit-${c.id}`}
                          title="Edit firm"
                        >
                          <Pencil className="w-3 h-3" strokeWidth={1.75} />
                          Edit
                        </button>
                        <button
                          onClick={() => impersonateCompany(c)}
                          className="flex-1 px-2 py-1.5 text-[11px] text-ink-lighter hover:text-forest hover:bg-forest/5 inline-flex items-center justify-center gap-1"
                          data-testid={`sa-impersonate-${c.id}`}
                          title="Sign in as this accountant"
                        >
                          <UserCheck className="w-3 h-3" strokeWidth={1.75} />
                          Sign in
                        </button>
                        <button
                          onClick={() => removeCompany(c)}
                          className="flex-1 px-2 py-1.5 text-[11px] text-ink-lighter hover:text-destructive hover:bg-destructive/5 inline-flex items-center justify-center gap-1"
                          data-testid={`sa-company-delete-${c.id}`}
                          title="Delete firm"
                        >
                          <Trash2 className="w-3 h-3" strokeWidth={1.75} />
                          Delete
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Client table */}
          <div className="lg:col-span-3 card-surface overflow-hidden border border-black">
            <div className="px-5 py-4 border-b border-border flex items-center gap-3 flex-wrap">
              <div>
                <div className="overline">Clients</div>
                <div className="font-heading text-lg font-medium">
                  {companyFilter === "all" ? "All clients" : companies.find(c => c.id === companyFilter)?.business_name || "Selected company"}
                  <span className="ml-2 text-sm text-ink-lighter num">({clients.length})</span>
                </div>
              </div>
              <div className="flex-1" />
              <div className="relative">
                <Search className="w-3.5 h-3.5 text-ink-lighter absolute left-2.5 top-1/2 -translate-y-1/2" />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search…"
                  className="bg-white border border-border rounded-md pl-8 pr-3 py-1.5 text-sm w-52 focus:ring-2 focus:ring-forest/20 focus:border-forest outline-none"
                  data-testid="sa-search"
                />
              </div>
              <button
                onClick={() => setShowAdd(true)}
                className="btn-forest text-sm inline-flex items-center gap-1.5"
                data-testid="sa-add-client-btn"
              >
                <Plus className="w-3.5 h-3.5" />
                Add client
              </button>
            </div>
            {loading && clients.length === 0 ? (
              <div className="p-10 text-center text-sm text-ink-lighter">Loading…</div>
            ) : clients.length === 0 ? (
              <div className="p-10 text-center text-sm text-ink-lighter">No clients here.</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-paper text-xs uppercase text-ink-lighter tracking-wider">
                  <tr>
                    <th className="text-left px-5 py-2.5 font-medium">Business</th>
                    <th className="text-left px-5 py-2.5 font-medium">Email</th>
                    <th className="text-left px-5 py-2.5 font-medium">Owner (company)</th>
                    <th className="text-right px-5 py-2.5 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {clients.map((c) => (
                    <tr key={c.id} data-testid={`sa-client-row-${c.id}`} className="hover:bg-paper/40">
                      <td className="px-5 py-3">
                        <div className="font-medium">{c.business_name || "(no name)"}</div>
                        {c.name && <div className="text-xs text-ink-lighter">{c.name}</div>}
                      </td>
                      <td className="px-5 py-3 text-ink-light">{c.email}</td>
                      <td className="px-5 py-3">
                        <div className="text-ink">{c.owner?.business_name || "—"}</div>
                        <div className="text-[10px] text-ink-lighter truncate max-w-[220px]">{c.owner?.email}</div>
                      </td>
                      <td className="px-5 py-3">
                        <div className="flex justify-end gap-1">
                          <button
                            onClick={() => setEditing(c)}
                            className="p-1.5 rounded-md text-ink-lighter hover:text-forest hover:bg-forest/5"
                            data-testid={`sa-edit-${c.id}`}
                            title="Edit client"
                          >
                            <Pencil className="w-4 h-4" strokeWidth={1.75} />
                          </button>
                          <button
                            onClick={() => impersonateClient(c)}
                            className="p-1.5 rounded-md text-ink-lighter hover:text-forest hover:bg-forest/5"
                            data-testid={`sa-impersonate-client-${c.id}`}
                            title="Sign in as this client"
                          >
                            <UserCheck className="w-4 h-4" strokeWidth={1.75} />
                          </button>
                          <button
                            onClick={() => setMoving(c)}
                            className="p-1.5 rounded-md text-ink-lighter hover:text-forest hover:bg-forest/5"
                            data-testid={`sa-move-${c.id}`}
                            title="Move to another company"
                          >
                            <Move className="w-4 h-4" strokeWidth={1.75} />
                          </button>
                          <button
                            onClick={() => remove(c)}
                            className="p-1.5 rounded-md text-ink-lighter hover:text-destructive hover:bg-destructive/5"
                            data-testid={`sa-delete-${c.id}`}
                            title="Delete client"
                          >
                            <Trash2 className="w-4 h-4" strokeWidth={1.75} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {showAdd && (
        <AddClientModal
          companies={companies}
          defaultCompanyId={companyFilter === "all" ? "" : companyFilter}
          onClose={() => setShowAdd(false)}
          onSaved={() => { setShowAdd(false); load(); }}
        />
      )}
      {moving && (
        <MoveClientModal
          client={moving}
          companies={companies}
          onClose={() => setMoving(null)}
          onSaved={() => { setMoving(null); load(); }}
        />
      )}
      {editing && (
        <EditClientModal
          client={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
      {editingCompany && (
        <EditCompanyModal
          company={editingCompany}
          onClose={() => setEditingCompany(null)}
          onSaved={() => { setEditingCompany(null); load(); }}
        />
      )}
      {showLog && <AuditLogModal onClose={() => setShowLog(false)} />}
    </div>
  );
}

const TILE_TONES = {
  forest: { bg: "bg-forest/10", border: "border-forest/20", icon: "text-forest", label: "text-forest" },
  sky:    { bg: "bg-sky-100",   border: "border-sky-200",   icon: "text-sky-700",   label: "text-sky-800" },
  ochre:  { bg: "bg-ochre/10",  border: "border-ochre/25",  icon: "text-ochre",     label: "text-ochre" },
  violet: { bg: "bg-violet-100",border: "border-violet-200",icon: "text-violet-700",label: "text-violet-800" },
  rose:   { bg: "bg-rose-100",  border: "border-rose-200",  icon: "text-rose-700",  label: "text-rose-800" },
};

function OverviewTile({ label, value, icon: Icon, testid, accent = "", tone = "forest" }) {
  const t = TILE_TONES[tone] || TILE_TONES.forest;
  return (
    <div className={`rounded-2xl border-2 border-black ${t.bg} p-4 shadow-sm`} data-testid={testid}>
      <div className="flex items-start justify-between">
        <div className={`text-[10px] uppercase tracking-wider font-medium ${t.label}`}>{label}</div>
        <Icon className={`w-3.5 h-3.5 ${t.icon}`} strokeWidth={1.75} />
      </div>
      <div className={`font-heading text-3xl num mt-1 ${accent || "text-ink"}`}>{value ?? "—"}</div>
    </div>
  );
}

// ---------------- Analytics ----------------

const PIE_COLORS = [
  "#0F4C3A", "#B4741E", "#1E3A5F", "#4A2B57", "#7A2E3F",
  "#1E5A5A", "#0369A1", "#7C3AED", "#B45309", "#065F46", "#78716C",
];

function AnalyticsSection() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [weeks, setWeeks] = useState(12);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/super-admin/analytics", { params: { weeks } });
        if (!cancelled) setData(data);
      } catch (e) {
        if (!cancelled) setErr(formatApiErrorDetail(e.response?.data?.detail) || "Could not load analytics");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [weeks]);

  if (loading && !data) {
    return (
      <div className="card-surface p-6 border border-black flex items-center gap-2 text-ink-lighter text-sm" data-testid="analytics-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading analytics…
      </div>
    );
  }
  if (err || !data) {
    return (
      <div className="card-surface p-6 border border-black flex items-center gap-2 text-ochre text-sm" data-testid="analytics-error">
        <AlertCircle className="w-4 h-4" /> {err || "No analytics available."}
      </div>
    );
  }

  const { growth, industries, top_firms, approvals, impersonation } = data;
  const RANGE_OPTIONS = [
    { w: 4, label: "4w" },
    { w: 12, label: "12w" },
    { w: 26, label: "26w" },
    { w: 52, label: "52w" },
  ];

  return (
    <div className="space-y-4" data-testid="analytics-section">
      <div className="grid lg:grid-cols-2 gap-4">
        {/* Approvals funnel */}
        <div className="card-surface p-6 border border-black">
          <div className="overline text-forest mb-0.5">Signup funnel</div>
          <div className="font-heading text-lg font-medium mb-4">Approval throughput</div>
          <div className="grid grid-cols-3 gap-3" data-testid="analytics-approvals">
            <ApprovalTile label="Pending" value={approvals.pending} tone="ochre" />
            <ApprovalTile label="Approved" value={approvals.approved} tone="forest" />
            <ApprovalTile label="Rejected" value={approvals.rejected} tone="rose" />
          </div>
          <div className="mt-4 pt-4 border-t border-border flex items-center gap-2 text-xs text-ink-light">
            <Clock className="w-3.5 h-3.5" />
            Median time to approval:{" "}
            <span className="font-medium text-ink num" data-testid="analytics-median-hours">
              {approvals.median_hours_to_approval == null
                ? "—"
                : approvals.median_hours_to_approval < 1
                  ? `${Math.round(approvals.median_hours_to_approval * 60)} min`
                  : `${approvals.median_hours_to_approval} hr`}
            </span>
          </div>
        </div>

        {/* Impersonation last 30d */}
        <div className="card-surface p-6 border border-black">
          <div className="overline text-forest mb-0.5">Compliance</div>
          <div className="font-heading text-lg font-medium mb-4">Impersonation · last 30 days</div>
          <div className="grid grid-cols-2 gap-3" data-testid="analytics-impersonation">
            <ApprovalTile
              label="By accountants"
              value={impersonation.accountant || 0}
              icon={UserCheck}
              tone="sky"
            />
            <ApprovalTile
              label="By super-admin"
              value={impersonation.super_admin || 0}
              icon={Shield}
              tone="violet"
            />
          </div>
          <div className="mt-4 pt-4 border-t border-border flex items-center gap-2 text-xs text-ink-light">
            <Activity className="w-3.5 h-3.5" />
            Every impersonation is logged with started/ended timestamps for audit.
          </div>
        </div>
      </div>

      {/* Growth line chart */}
      <div className="card-surface p-6 border border-black">
        <div className="flex items-start justify-between mb-4 gap-4 flex-wrap">
          <div>
            <div className="overline flex items-center gap-1.5 text-forest">
              <TrendingUp className="w-3 h-3" strokeWidth={2} /> Growth · last {weeks} weeks
            </div>
            <div className="font-heading text-lg font-medium mt-0.5">Weekly signups &amp; uploads</div>
          </div>
          <div
            className="inline-flex rounded-lg border border-border bg-paper p-0.5"
            data-testid="analytics-range-switcher"
          >
            {RANGE_OPTIONS.map((opt) => (
              <button
                key={opt.w}
                type="button"
                onClick={() => setWeeks(opt.w)}
                className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                  weeks === opt.w
                    ? "bg-forest text-white shadow-sm"
                    : "text-ink-light hover:text-ink"
                }`}
                data-testid={`analytics-range-${opt.w}`}
                aria-pressed={weeks === opt.w}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
        <div className="h-64" data-testid="analytics-growth-chart">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={growth} margin={{ top: 6, right: 8, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E3" />
              <XAxis
                dataKey="week"
                stroke="#8A8A8A"
                fontSize={11}
                interval={weeks > 26 ? Math.floor(weeks / 12) : 0}
              />
              <YAxis stroke="#8A8A8A" fontSize={11} allowDecimals={false} />
              <RTooltip
                contentStyle={{ background: "#fff", border: "1px solid #E5E5E3", borderRadius: 8, fontSize: 12 }}
                labelStyle={{ color: "#1A1A1A", fontWeight: 500 }}
              />
              <Legend wrapperStyle={{ fontSize: 11, paddingTop: 6 }} />
              <Line type="monotone" dataKey="new_accountants" name="Accountants" stroke="#0F4C3A" strokeWidth={2} dot={weeks <= 12 ? { r: 2 } : false} />
              <Line type="monotone" dataKey="new_clients" name="Clients" stroke="#1E3A5F" strokeWidth={2} dot={weeks <= 12 ? { r: 2 } : false} />
              <Line type="monotone" dataKey="statements" name="Statements" stroke="#B4741E" strokeWidth={2} dot={weeks <= 12 ? { r: 2 } : false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        {/* Industries pie */}
        <div className="card-surface p-6 border border-black">
          <div className="overline text-forest mb-0.5">Industry mix</div>
          <div className="font-heading text-lg font-medium mb-3">Client industries</div>
          {industries.length === 0 ? (
            <div className="text-sm text-ink-lighter italic py-8 text-center">No industry data yet.</div>
          ) : (
            <div className="h-64" data-testid="analytics-industries-chart">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={industries}
                    dataKey="count"
                    nameKey="industry"
                    outerRadius={80}
                    label={({ industry, count }) => `${industry} (${count})`}
                    labelLine={false}
                  >
                    {industries.map((_, i) => (
                      <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <RTooltip contentStyle={{ background: "#fff", border: "1px solid #E5E5E3", borderRadius: 8, fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        {/* Top firms */}
        <div className="card-surface p-6 border border-black">
          <div className="overline text-forest mb-0.5">Leaderboard</div>
          <div className="font-heading text-lg font-medium mb-3">Top firms by activity</div>
          {top_firms.length === 0 ? (
            <div className="text-sm text-ink-lighter italic py-8 text-center">No firms with activity yet.</div>
          ) : (
            <div className="divide-y divide-border" data-testid="analytics-top-firms">
              {top_firms.map((f, i) => (
                <div key={f.id} className="flex items-center justify-between py-2 text-sm" data-testid={`analytics-top-firm-${f.id}`}>
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="w-5 text-xs text-ink-lighter font-medium num">{i + 1}.</span>
                    <span className="font-medium truncate">{f.business_name}</span>
                  </div>
                  <div className="flex items-center gap-4 text-xs text-ink-light shrink-0 ml-4">
                    <span><span className="num font-semibold text-ink">{f.clients}</span> clients</span>
                    <span><span className="num font-semibold text-ink">{f.statements}</span> stmts</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const APPROVAL_TONES = {
  forest: { bg: "bg-forest/10", label: "text-forest", value: "text-forest" },
  ochre:  { bg: "bg-ochre/10",  label: "text-ochre",  value: "text-ochre" },
  rose:   { bg: "bg-rose-100",  label: "text-rose-700", value: "text-rose-700" },
  sky:    { bg: "bg-sky-100",   label: "text-sky-800", value: "text-sky-800" },
  violet: { bg: "bg-violet-100",label: "text-violet-800", value: "text-violet-800" },
};

function ApprovalTile({ label, value, tone = "forest", icon: Icon }) {
  const t = APPROVAL_TONES[tone] || APPROVAL_TONES.forest;
  return (
    <div className={`rounded-lg ${t.bg} p-3 border border-black`}>
      <div className={`text-[10px] uppercase tracking-wider font-medium flex items-center gap-1 ${t.label}`}>
        {Icon && <Icon className="w-3 h-3" strokeWidth={2} />}
        {label}
      </div>
      <div className={`font-heading text-2xl font-medium num mt-0.5 ${t.value}`}>{value ?? "—"}</div>
    </div>
  );
}

// ---------------- Add client modal ----------------

function AddClientModal({ companies, defaultCompanyId, onClose, onSaved }) {
  const [form, setForm] = useState({
    company_id: defaultCompanyId || "",
    email: "",
    business_name: "",
    name: "",
    phone: "",
    password: "",
  });
  const [saving, setSaving] = useState(false);
  const [createdPassword, setCreatedPassword] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.company_id) { toast.error("Pick a company first."); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      Object.keys(payload).forEach(k => { if (!payload[k]) delete payload[k]; });
      const { data } = await api.post("/super-admin/clients", payload);
      const initial = data.client.initial_password;
      if (initial) {
        setCreatedPassword({ email: data.client.email, password: initial });
      } else {
        toast.success("Client added");
        onSaved();
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not create");
    } finally {
      setSaving(false);
    }
  };

  if (createdPassword) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-ink/30 backdrop-blur-sm" onClick={onSaved}>
        <div className="bg-white rounded-2xl border border-border shadow-xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
          <div className="overline mb-1">Client created</div>
          <h3 className="font-heading text-xl font-medium mb-3">Save these credentials</h3>
          <p className="text-sm text-ink-light mb-4">Send this login to the client. You won&apos;t see the password again.</p>
          <div className="bg-paper rounded-lg p-4 space-y-2 font-mono text-xs">
            <div><span className="text-ink-lighter">Email: </span>{createdPassword.email}</div>
            <div><span className="text-ink-lighter">Password: </span>{createdPassword.password}</div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <button
              type="button"
              onClick={() => {
                navigator.clipboard.writeText(`${createdPassword.email} / ${createdPassword.password}`);
                toast.success("Copied");
              }}
              className="btn-secondary text-sm inline-flex items-center gap-1.5"
              data-testid="sa-copy-creds"
            >
              <ClipboardCopy className="w-3.5 h-3.5" /> Copy
            </button>
            <button onClick={onSaved} className="btn-forest text-sm" data-testid="sa-done-creds">Done</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-ink/30 backdrop-blur-sm" onClick={onClose} data-testid="sa-add-modal">
      <div className="bg-white rounded-2xl border border-border shadow-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 pt-6 pb-3 flex items-start justify-between border-b border-border">
          <div>
            <div className="overline mb-1">New client</div>
            <h3 className="font-heading text-xl font-medium">Add to a company</h3>
          </div>
          <button onClick={onClose} className="p-1 text-ink-lighter hover:text-ink"><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-3">
          <label className="block text-sm">
            <span className="font-medium">Company</span>
            <select
              required
              value={form.company_id}
              onChange={(e) => setForm({ ...form, company_id: e.target.value })}
              className="w-full mt-1 bg-white border border-border rounded-lg px-3 py-2"
              data-testid="sa-add-company"
            >
              <option value="">Pick a company…</option>
              {companies.filter(c => c.kind === "super_admin").map((c) => (
                <option key={c.id} value={c.id}>🛡️  {c.business_name}</option>
              ))}
              {companies.some(c => c.kind === "super_admin") && companies.some(c => c.kind !== "super_admin") && (
                <option disabled>──────────────</option>
              )}
              {companies.filter(c => c.kind !== "super_admin").map((c) => (
                <option key={c.id} value={c.id}>{c.business_name || c.email}</option>
              ))}
            </select>
          </label>
          <label className="block text-sm">
            <span className="font-medium">Business name</span>
            <input required value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })}
              className="w-full mt-1 bg-white border border-border rounded-lg px-3 py-2" data-testid="sa-add-business" />
          </label>
          <label className="block text-sm">
            <span className="font-medium">Client email</span>
            <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full mt-1 bg-white border border-border rounded-lg px-3 py-2" data-testid="sa-add-email" />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block text-sm">
              <span className="font-medium">Contact name</span>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full mt-1 bg-white border border-border rounded-lg px-3 py-2" />
            </label>
            <label className="block text-sm">
              <span className="font-medium">Phone</span>
              <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="w-full mt-1 bg-white border border-border rounded-lg px-3 py-2" />
            </label>
          </div>
          <label className="block text-sm">
            <span className="font-medium">Password <span className="text-ink-lighter font-normal">(leave blank to auto-generate)</span></span>
            <input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="w-full mt-1 bg-white border border-border rounded-lg px-3 py-2" data-testid="sa-add-password" />
          </label>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
            <button type="submit" disabled={saving} className="btn-forest inline-flex items-center gap-2" data-testid="sa-add-submit">
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              Create client
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ---------------- Move client modal ----------------

function MoveClientModal({ client, companies, onClose, onSaved }) {
  const [target, setTarget] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const available = useMemo(() => companies.filter(c => c.id !== client.owner_id), [companies, client.owner_id]);
  const platform = available.find(c => c.kind === "super_admin");
  const accountants = available.filter(c => c.kind !== "super_admin");

  const submit = async (e) => {
    e.preventDefault();
    if (!target) return;
    setSaving(true);
    try {
      const { data } = await api.post(`/super-admin/clients/${client.id}/move`, {
        new_company_id: target,
        reason: reason || undefined,
      });
      toast.success(`Moved. Re-parented ${data.moved_counts.statements} statements, ${data.moved_counts.tasks} tasks, ${data.moved_counts.notes} notes.`);
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Move failed");
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-ink/30 backdrop-blur-sm" onClick={onClose} data-testid="sa-move-modal">
      <div className="bg-white rounded-2xl border border-border shadow-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 pt-6 pb-3 flex items-start justify-between border-b border-border">
          <div>
            <div className="overline mb-1">Move client</div>
            <h3 className="font-heading text-xl font-medium">{client.business_name}</h3>
            <p className="text-sm text-ink-light mt-1">
              Current owner: <span className="font-medium">{client.owner?.business_name || "—"}</span>
            </p>
          </div>
          <button onClick={onClose} className="p-1 text-ink-lighter hover:text-ink"><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <div className="bg-ochre/10 border border-ochre/30 rounded-lg p-3 text-xs text-ochre">
            All statements, tasks, and notes for this client will be re-parented to the new company. Messages and impersonation logs stay as historical audit trail.
          </div>
          <label className="block text-sm">
            <span className="font-medium">Move to</span>
            <select
              required
              value={target}
              onChange={(e) => setTarget(e.target.value)}
              className="w-full mt-1 bg-white border border-border rounded-lg px-3 py-2"
              data-testid="sa-move-target"
            >
              <option value="">Pick a destination…</option>
              {platform && (
                <option value={platform.id}>🛡️  {platform.business_name}</option>
              )}
              {accountants.length > 0 && platform && (
                <option disabled>──────────────</option>
              )}
              {accountants.map((c) => <option key={c.id} value={c.id}>{c.business_name || c.email}</option>)}
            </select>
          </label>
          <label className="block text-sm">
            <span className="font-medium">Reason <span className="text-ink-lighter font-normal">(logged for audit)</span></span>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              className="w-full mt-1 bg-white border border-border rounded-lg px-3 py-2"
              placeholder="e.g. Client requested transfer to new accountant"
              data-testid="sa-move-reason"
            />
          </label>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
            <button type="submit" disabled={saving || !target} className="btn-forest inline-flex items-center gap-2 disabled:opacity-40" data-testid="sa-move-submit">
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              <Check className="w-4 h-4" />
              Move client
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ---------------- Edit client modal ----------------

function EditClientModal({ client, onClose, onSaved }) {
  const [form, setForm] = useState({
    business_name: client.business_name || "",
    email: client.email || "",
    name: client.name || "",
    phone: client.phone || "",
    industry: client.industry || "",
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // Only send changed fields
      const patch = {};
      for (const k of Object.keys(form)) {
        const orig = client[k] || "";
        const curr = form[k] || "";
        if (curr !== orig) patch[k] = curr;
      }
      if (Object.keys(patch).length === 0) {
        toast.message("Nothing changed.");
        setSaving(false);
        return;
      }
      await api.patch(`/super-admin/clients/${client.id}`, patch);
      toast.success("Client updated.");
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-4"
      onClick={onClose}
      data-testid="sa-edit-client-modal"
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="w-full max-w-lg bg-white rounded-2xl shadow-xl p-6"
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="overline mb-1">Edit client</div>
            <div className="font-heading text-xl font-medium">{client.business_name || client.email}</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-md text-ink-lighter hover:text-ink hover:bg-paper"
            data-testid="sa-edit-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="space-y-3">
          <Field
            label="Business name"
            value={form.business_name}
            onChange={(v) => setForm((f) => ({ ...f, business_name: v }))}
            testid="sa-edit-business-name"
            required
          />
          <Field
            label="Email"
            type="email"
            value={form.email}
            onChange={(v) => setForm((f) => ({ ...f, email: v }))}
            testid="sa-edit-email"
            required
          />
          <Field
            label="Contact name (optional)"
            value={form.name}
            onChange={(v) => setForm((f) => ({ ...f, name: v }))}
            testid="sa-edit-name"
          />
          <Field
            label="Phone (optional)"
            value={form.phone}
            onChange={(v) => setForm((f) => ({ ...f, phone: v }))}
            testid="sa-edit-phone"
          />
          <Field
            label="Industry (optional)"
            value={form.industry}
            onChange={(v) => setForm((f) => ({ ...f, industry: v }))}
            testid="sa-edit-industry"
          />
        </div>

        <div className="mt-6 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-md text-sm text-ink-light hover:text-ink"
            data-testid="sa-edit-cancel"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="btn-forest text-sm inline-flex items-center gap-2 disabled:opacity-60"
            data-testid="sa-edit-save"
          >
            {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testid, required }) {
  return (
    <label className="block">
      <span className="block text-xs text-ink-light mb-1">{label}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        className="w-full px-3 py-2 rounded-md border border-border bg-white text-sm focus:outline-none focus:border-forest"
        data-testid={testid}
      />
    </label>
  );
}

// ---------------- Edit company modal ----------------

function EditCompanyModal({ company, onClose, onSaved }) {
  const [form, setForm] = useState({
    business_name: company.business_name || "",
    email: company.email || "",
    name: company.name || "",
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const patch = {};
      for (const k of Object.keys(form)) {
        const orig = company[k] || "";
        const curr = form[k] || "";
        if (curr !== orig) patch[k] = curr;
      }
      if (Object.keys(patch).length === 0) {
        toast.message("Nothing changed.");
        setSaving(false);
        return;
      }
      await api.patch(`/super-admin/companies/${company.id}`, patch);
      toast.success("Firm updated.");
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-4"
      onClick={onClose}
      data-testid="sa-edit-company-modal"
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="w-full max-w-lg bg-white rounded-2xl shadow-xl p-6"
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="overline mb-1">Edit firm</div>
            <div className="font-heading text-xl font-medium">{company.business_name || company.email}</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-md text-ink-lighter hover:text-ink hover:bg-paper"
            data-testid="sa-edit-company-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="space-y-3">
          <Field
            label="Firm name"
            value={form.business_name}
            onChange={(v) => setForm((f) => ({ ...f, business_name: v }))}
            testid="sa-edit-company-business-name"
            required
          />
          <Field
            label="Email"
            type="email"
            value={form.email}
            onChange={(v) => setForm((f) => ({ ...f, email: v }))}
            testid="sa-edit-company-email"
            required
          />
          <Field
            label="Contact name (optional)"
            value={form.name}
            onChange={(v) => setForm((f) => ({ ...f, name: v }))}
            testid="sa-edit-company-name"
          />
        </div>

        <div className="mt-6 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-md text-sm text-ink-light hover:text-ink"
            data-testid="sa-edit-company-cancel"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="btn-forest text-sm inline-flex items-center gap-2 disabled:opacity-60"
            data-testid="sa-edit-company-save"
          >
            {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </form>
    </div>
  );
}

// ---------------- Audit log modal ----------------

function AuditLogModal({ onClose }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/super-admin/audit-log")
      .then(({ data }) => setRows(data.actions || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, []);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-ink/30 backdrop-blur-sm" onClick={onClose} data-testid="sa-audit-modal">
      <div className="bg-white rounded-2xl border border-border shadow-xl w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 flex items-center justify-between border-b border-border">
          <div>
            <div className="overline">Audit</div>
            <div className="font-heading text-lg font-medium">Super-admin actions</div>
          </div>
          <button onClick={onClose} className="p-1 text-ink-lighter hover:text-ink"><X className="w-4 h-4" /></button>
        </div>
        <div className="overflow-y-auto flex-1 divide-y divide-border">
          {loading ? (
            <div className="p-6 text-ink-lighter text-sm">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="p-6 text-ink-lighter text-sm text-center">No actions logged yet.</div>
          ) : rows.map((r) => (
            <div key={r.id} className="p-4" data-testid={`sa-audit-${r.id}`}>
              <div className="flex items-center justify-between">
                <div className="font-medium text-sm capitalize">{r.action.replace("_", " ")}</div>
                <div className="text-[10px] text-ink-lighter num">{r.at.slice(0, 16).replace("T", " ")}</div>
              </div>
              <div className="text-xs text-ink-lighter mt-0.5">by {r.admin_email}</div>
              <pre className="text-[11px] text-ink-light bg-paper rounded p-2 mt-2 overflow-x-auto">{JSON.stringify(r.detail, null, 2)}</pre>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ---------------- Unassigned queue ----------------

function UnassignedQueue({ clients, accountants, onAssigned }) {
  const [selections, setSelections] = useState({}); // { [clientId]: accountantId }
  const [busyId, setBusyId] = useState(null);
  const [suggesting, setSuggesting] = useState(null); // clientId currently loading
  const [suggestions, setSuggestions] = useState({}); // { [clientId]: [{accountant_id, business_name, confidence, why}] }

  const suggest = async (c) => {
    setSuggesting(c.id);
    try {
      const { data } = await api.post(`/super-admin/clients/${c.id}/suggest-accountant`);
      setSuggestions((prev) => ({ ...prev, [c.id]: { list: data.suggestions || [], reasoning: data.reasoning || "" } }));
      // Auto-select the top suggestion
      const top = (data.suggestions || [])[0];
      if (top) setSelections((prev) => ({ ...prev, [c.id]: top.accountant_id }));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "AI suggestion failed");
    } finally {
      setSuggesting(null);
    }
  };

  const approve = async (c) => {
    const target = selections[c.id];
    setBusyId(c.id);
    try {
      const body = { accountant_id: target || undefined, reason: target ? "Approved and assigned" : "Approved (keep on Platform)" };
      await api.post(`/super-admin/clients/${c.id}/approve`, body);
      toast.success(target
        ? `${c.business_name} approved and assigned`
        : `${c.business_name} approved`);
      onAssigned();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not approve");
    } finally {
      setBusyId(null);
    }
  };

  const reject = async (c) => {
    const reason = window.prompt(`Reject ${c.business_name}? Provide a short reason (optional):`, "");
    if (reason === null) return; // cancelled
    setBusyId(c.id);
    try {
      await api.post(`/super-admin/clients/${c.id}/reject`, { reason });
      toast.success(`${c.business_name} rejected`);
      onAssigned();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not reject");
    } finally {
      setBusyId(null);
    }
  };

  const pendingCount = clients.filter(c => (c.approval_status || "pending") === "pending").length;

  return (
    <div className="card-surface overflow-hidden border-l-4 border-l-ochre" data-testid="unassigned-queue">
      <div className="px-5 py-3 border-b border-border bg-ochre/5 flex items-center gap-3">
        <div className="w-8 h-8 rounded-lg bg-ochre/15 text-ochre flex items-center justify-center shrink-0">
          <Inbox className="w-4 h-4" strokeWidth={2} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="overline text-ochre">Attention</div>
          <div className="font-heading text-lg font-medium">Pending approval</div>
        </div>
        <div className="text-xs text-ink-lighter shrink-0 num">
          {pendingCount} awaiting review
        </div>
      </div>
      <table className="w-full text-sm">
        <thead className="bg-paper text-xs uppercase text-ink-lighter tracking-wider">
          <tr>
            <th className="text-left px-5 py-2.5 font-medium">Business</th>
            <th className="text-left px-5 py-2.5 font-medium">Contact</th>
            <th className="text-left px-5 py-2.5 font-medium">Signed up</th>
            <th className="text-left px-5 py-2.5 font-medium">Assign to <span className="text-ink-lighter font-normal normal-case">(optional)</span></th>
            <th className="text-right px-5 py-2.5 font-medium">Decision</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {clients.map((c) => {
            const status = c.approval_status || "pending";
            return (
              <tr key={c.id} data-testid={`unassigned-row-${c.id}`} className="hover:bg-paper/40">
                <td className="px-5 py-3">
                  <div className="font-medium flex items-center gap-2 flex-wrap">
                    {c.business_name}
                    {status === "pending" && (
                      <span className="text-[10px] font-medium rounded-full px-1.5 py-0.5 bg-ochre/15 text-ochre">
                        Pending
                      </span>
                    )}
                    {c.industry && (
                      <span className="text-[10px] text-ink-lighter bg-paper border border-border rounded-full px-1.5 py-0.5" data-testid={`unassigned-industry-${c.id}`}>
                        {c.industry}
                      </span>
                    )}
                  </div>
                  {c.name && <div className="text-xs text-ink-lighter">{c.name}</div>}
                </td>
                <td className="px-5 py-3">
                  <div className="text-ink-light">{c.email}</div>
                  {c.phone && <div className="text-xs text-ink-lighter">{c.phone}</div>}
                </td>
                <td className="px-5 py-3 num text-xs text-ink-light">
                  {c.created_at ? c.created_at.slice(0, 10) : "—"}
                </td>
                <td className="px-5 py-3">
                  <select
                    value={selections[c.id] || ""}
                    onChange={(e) => setSelections((prev) => ({ ...prev, [c.id]: e.target.value }))}
                    className="w-full bg-white border border-border rounded-md px-2 py-1 text-sm min-w-[200px]"
                    data-testid={`unassigned-select-${c.id}`}
                  >
                    <option value="">Keep on Platform</option>
                    {accountants.map((a) => (
                      <option key={a.id} value={a.id}>{a.business_name || a.email}</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => suggest(c)}
                    disabled={suggesting === c.id}
                    className="mt-1 text-[10px] text-forest hover:underline inline-flex items-center gap-1"
                    data-testid={`unassigned-suggest-${c.id}`}
                  >
                    {suggesting === c.id ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <Sparkles className="w-2.5 h-2.5" />}
                    Suggest fit (AI)
                  </button>
                  {suggestions[c.id]?.list?.[0] && (
                    <div className="mt-1 text-[10px] text-ink-lighter leading-snug" data-testid={`unassigned-suggestion-${c.id}`}>
                      Top pick: <span className="text-ink-light font-medium">{suggestions[c.id].list[0].accountant_business_name}</span>
                      <span className="text-forest ml-1">({suggestions[c.id].list[0].confidence})</span>
                      <div className="italic">{suggestions[c.id].list[0].why}</div>
                    </div>
                  )}
                </td>
                <td className="px-5 py-3 text-right">
                  <div className="inline-flex gap-1.5">
                    <button
                      type="button"
                      onClick={() => reject(c)}
                      disabled={busyId === c.id}
                      className="btn-secondary text-xs inline-flex items-center gap-1 text-destructive hover:text-destructive disabled:opacity-40"
                      data-testid={`unassigned-reject-${c.id}`}
                    >
                      <X className="w-3 h-3" />
                      Reject
                    </button>
                    <button
                      type="button"
                      onClick={() => approve(c)}
                      disabled={busyId === c.id}
                      className="btn-forest text-xs inline-flex items-center gap-1.5 disabled:opacity-40"
                      data-testid={`unassigned-approve-${c.id}`}
                    >
                      {busyId === c.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                      Approve
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

